import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Apple, Play, Mail, Phone, Sparkles, Star, Quote } from "lucide-react";import { toast } from "sonner";
import { api, imgUrl, apiError } from "@/lib/api";
import { socialLinksOf, socialIcon } from "@/lib/social";
import Marquee from "@/components/Marquee";
import { Reveal, ImageReveal } from "@/components/Reveal";
import { Field, TextArea, SubmitButton } from "@/components/Field";

const lineAnim = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 1.1, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Home() {
  const [settings, setSettings] = useState({});
  const [categories, setCategories] = useState([]);
  const [reviews, setReviews] = useState([]);
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      {settings.banner_enabled && settings.banner_text?.trim() && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
          data-testid="seasonal-banner" className="max-w-[1400px] mx-auto px-6 md:px-10 pt-24">
          {(() => {
            const inner = (
              <>
                <Sparkles size={13} strokeWidth={1.5} className="text-[#E4C57A] shrink-0" />
                <span className="font-serif italic text-sm md:text-base">{settings.banner_text}</span>
                {settings.banner_cta && (
                  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#E4C57A] shrink-0">
                    {settings.banner_cta} <ArrowRight size={11} strokeWidth={1.5} />
                  </span>
                )}
              </>
            );
            const cls = "flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-[#1A1A1A] text-[#F9F9F8] px-6 py-3.5 rounded-full text-center";
            if (settings.banner_link && /^https?:\/\//.test(settings.banner_link)) {
              return <a href={settings.banner_link} data-testid="seasonal-banner-link" className={`${cls} hover:bg-[#2A2A2A] transition-colors duration-300`}>{inner}</a>;
            }
            if (settings.banner_link) {
              return <Link to={settings.banner_link} data-testid="seasonal-banner-link" className={`${cls} hover:bg-[#2A2A2A] transition-colors duration-300`}>{inner}</Link>;
            }
            return <div className={cls}>{inner}</div>;
          })()}
        </motion.div>
      )}
      {/* HERO */}
      <section ref={heroRef} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 w-full grid lg:grid-cols-12 gap-10 items-center pt-28 pb-16">
          <div className="lg:col-span-7 relative z-10">
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 1 }}
              className="text-[11px] uppercase tracking-[0.3em] text-[#7A7A7A] mb-8">
              Photographer · Realtor · Educator — Los Angeles
            </motion.p>
            <h1 className="font-serif leading-[0.95] tracking-tight text-[#1A1A1A]">
              <span className="block overflow-hidden">
                <motion.span custom={0} variants={lineAnim} initial="hidden" animate="show" className="block text-7xl sm:text-8xl lg:text-[10rem] font-light italic">Bahare</motion.span>
              </span>
            </h1>
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.9 }}
              data-testid="hero-subtitle" className="mt-8 text-base md:text-lg text-[#4A4A4A] font-light max-w-md leading-relaxed">
              {settings.hero_subtitle || "Timeless photography for your most meaningful moments."}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9 }}
              className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/book" data-testid="hero-book-btn"
                className="bg-[#1A1A1A] text-[#F9F9F8] text-[11px] uppercase tracking-[0.22em] px-9 py-4 rounded-full hover:bg-[#4A4A4A] transition-colors duration-300 inline-flex items-center gap-2">
                Book a Session <ArrowRight size={13} strokeWidth={1.5} />
              </Link>
              <Link to="/photography" data-testid="hero-portfolio-btn"
                className="text-[11px] uppercase tracking-[0.22em] px-9 py-4 rounded-full border border-[#1A1A1A]/20 hover:border-[#1A1A1A] transition-colors duration-300">
                View Portfolio
              </Link>
            </motion.div>
          </div>
          <div className="lg:col-span-5 relative">
            <motion.div initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden">
              <motion.img src={imgUrl(settings.hero_image) || undefined} alt="Portrait by Bahare Salehnia" style={{ y: imgY }}
                data-testid="hero-image" className="w-full aspect-[3/4] object-cover scale-110" />
            </motion.div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
          <span className="text-[9px] uppercase tracking-[0.3em] text-[#7A7A7A]">Scroll</span>
          <div className="w-px h-10 bg-[#1A1A1A]/30 animate-scroll-pulse" />
        </div>
      </section>

      <Marquee items={categories.length ? categories.map((c) => c.name) : ["Maternity", "Newborn", "Family", "Couples", "Portrait", "Branding", "Events", "Products"]} />

      {/* THREE AREAS */}
      <section data-testid="three-areas" className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-36">
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#7A7A7A] mb-4">One vision, three crafts</p>
          <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tight max-w-2xl leading-[1.05]">
            Every frame, every home, every lesson — <span className="italic">considered.</span>
          </h2>
        </Reveal>

        {/* Photography — dominant */}
        <Reveal className="mt-20">
          <Link to="/photography" data-testid="area-photography" className="group grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8 overflow-hidden">
              <img src={imgUrl(settings.craft_image || categories[0]?.cover_image || settings.hero_image) || undefined} alt="Photography by Bahare Salehnia" loading="lazy"
                className="w-full aspect-[16/10] object-cover group-hover:scale-[1.03] transition-transform duration-[1200ms] ease-out" />
            </div>
            <div className="lg:col-span-4 lg:pb-4">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#7A7A7A] mb-3">01 — Primary craft</p>
              <h3 className="font-serif text-4xl md:text-5xl font-light mb-4">Photography</h3>
              <p className="text-sm text-[#4A4A4A] leading-relaxed mb-6 max-w-xs">
                Maternity, newborn, family, couples, portraits, branding and more — honest imagery with an editorial soul.
              </p>
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] border-b border-[#1A1A1A] pb-1 group-hover:gap-3 transition-all duration-300">
                Explore Photography <ArrowUpRight size={13} strokeWidth={1.5} />
              </span>
            </div>
          </Link>
        </Reveal>

        {/* Real Estate + Education — subordinate */}
        <div className="mt-16 grid md:grid-cols-2 gap-8">
          <Reveal delay={0.1}>
            <Link to="/workshops" data-testid="area-workshops" className="group block border border-[#1A1A1A]/10 p-8 md:p-10 hover:border-[#1A1A1A]/30 transition-colors duration-500 bg-white/40">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#7A7A7A] mb-3">02</p>
              <h3 className="font-serif text-3xl font-light mb-3">Workshops</h3>
              <p className="text-sm text-[#4A4A4A] leading-relaxed mb-6 max-w-sm">
                Small-group, hands-on photography workshops in Los Angeles — limited seats, real practice, honest feedback.
              </p>
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#7A7A7A] group-hover:text-[#1A1A1A] group-hover:gap-3 transition-all duration-300">
                Join a Workshop <ArrowUpRight size={13} strokeWidth={1.5} />
              </span>
            </Link>
          </Reveal>
          <Reveal delay={0.2}>
            <Link to="/learn" data-testid="area-education" className="group block border border-[#1A1A1A]/10 p-8 md:p-10 hover:border-[#1A1A1A]/30 transition-colors duration-500 bg-white/40">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#7A7A7A] mb-3">03</p>
              <h3 className="font-serif text-3xl font-light mb-3">Education</h3>
              <p className="text-sm text-[#4A4A4A] leading-relaxed mb-6 max-w-sm">
                Photography, content creation, mobile and camera training — private classes and one-on-one mentoring.
              </p>
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#7A7A7A] group-hover:text-[#1A1A1A] group-hover:gap-3 transition-all duration-300">
                Learn With Bahare <ArrowUpRight size={13} strokeWidth={1.5} />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CATEGORY BENTO PREVIEW */}
      {categories.length > 0 && (
        <section data-testid="home-categories" className="max-w-[1400px] mx-auto px-6 md:px-10 pb-28 md:pb-36">
          <Reveal className="flex items-end justify-between mb-14">
            <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight">Sessions</h2>
            <Link to="/photography" className="text-[11px] uppercase tracking-[0.22em] text-[#7A7A7A] hover:text-[#1A1A1A] transition-colors inline-flex items-center gap-2">
              All categories <ArrowRight size={13} strokeWidth={1.5} />
            </Link>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {categories.slice(0, 4).map((c, i) => (
              <Reveal key={c.id} delay={i * 0.08} className={i === 0 ? "col-span-2 row-span-2" : ""}>
                <Link to={`/photography/${c.slug}`} data-testid={`home-category-${c.slug}`} className="group block relative overflow-hidden h-full">
                  <img src={imgUrl(c.cover_image)} alt={c.name} loading="lazy"
                    className={`w-full object-cover group-hover:scale-[1.03] transition-transform duration-[1200ms] ease-out ${i === 0 ? "aspect-[4/3] lg:aspect-auto lg:h-full" : "aspect-square"}`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/50 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 p-5">
                    <p className="font-serif text-xl md:text-2xl text-[#F9F9F8]">{c.name}</p>
                    {c.starting_price && <p className="text-[10px] uppercase tracking-[0.2em] text-[#F9F9F8]/70 mt-1">{c.starting_price}</p>}
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* ABOUT */}
      <section id="about" data-testid="about-section" className="bg-[#F4F4F1] border-y border-[#1A1A1A]/8">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-36 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <ImageReveal src={imgUrl(settings.about_image)} alt="Bahare Salehnia" className="aspect-[3/4]" />
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal>
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#7A7A7A] mb-6">About</p>
              <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight mb-8 leading-[1.08]">
                Hello, I'm <span className="italic">Bahare.</span>
              </h2>
              <p className="text-base text-[#4A4A4A] leading-relaxed max-w-lg mb-10">
                {settings.about_text || "Photographer, realtor and educator based in Los Angeles."}
              </p>
              {socialLinksOf(settings).length > 0 && (
                <div className="flex flex-wrap gap-3 mb-8" data-testid="about-social-links">
                  {socialLinksOf(settings).map((l, i) => {
                    const Icon = socialIcon(l.url);
                    return (
                      <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`about-social-${i}`}
                        className="inline-flex items-center gap-2 border border-[#1A1A1A]/20 text-[11px] uppercase tracking-[0.2em] px-7 py-3.5 rounded-full hover:border-[#1A1A1A] transition-colors">
                        <Icon size={15} strokeWidth={1.5} />
                        {l.label || `@${l.url.replace(/\/+$/, "").split("/").pop()}`}
                      </a>
                    );
                  })}
                </div>
              )}
              {(settings.app_store_url || settings.google_play_url) && (                <div className="flex flex-wrap gap-3 mb-10" data-testid="app-download-links">
                  {settings.app_store_url && (
                    <a href={settings.app_store_url} target="_blank" rel="noopener noreferrer" data-testid="app-store-btn"
                      className="inline-flex items-center gap-2 border border-[#1A1A1A]/20 text-[11px] uppercase tracking-[0.2em] px-7 py-3.5 rounded-full hover:border-[#1A1A1A] transition-colors">
                      <Apple size={15} strokeWidth={1.5} /> App Store
                    </a>
                  )}
                  {settings.google_play_url && (
                    <a href={settings.google_play_url} target="_blank" rel="noopener noreferrer" data-testid="google-play-btn"
                      className="inline-flex items-center gap-2 border border-[#1A1A1A]/20 text-[11px] uppercase tracking-[0.2em] px-7 py-3.5 rounded-full hover:border-[#1A1A1A] transition-colors">
                      <Play size={15} strokeWidth={1.5} /> Google Play
                    </a>
                  )}
                </div>
              )}
              <Link to="/book" data-testid="about-book-btn"
                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] border-b border-[#1A1A1A] pb-1 hover:gap-3 transition-all duration-300">
                Work With Me <ArrowUpRight size={13} strokeWidth={1.5} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* KIND WORDS */}
      {reviews.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24" data-testid="reviews-section">
          <Reveal>
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#7A7A7A] mb-4">Kind words</p>
                <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight">Loved by the people<br /><span className="italic">in front of my lens.</span></h2>
              </div>
              <Quote size={36} strokeWidth={1} className="text-[#E5E5E2] hidden md:block" />
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-5">
            {reviews.slice(0, 6).map((r, i) => (
              <Reveal key={r.id} delay={0.06 * i}>
                <div className="h-full rounded-[1.5rem] p-8 flex flex-col" data-testid={`review-card-${i}`}
                  style={{ backgroundColor: ["#E4EDDF", "#F7E3D7", "#E7E3F2"][i % 3] }}>
                  <div className="flex gap-0.5 text-[#C9A24B] mb-5">
                    {Array.from({ length: r.stars || 5 }).map((_, j) => <Star key={j} size={13} fill="currentColor" strokeWidth={0} />)}
                  </div>
                  <p className="font-serif text-lg italic leading-relaxed flex-1">“{r.text}”</p>
                  <p className="text-[11px] uppercase tracking-[0.2em] text-[#4A4A4A] mt-6">
                    {r.name}{r.session_type ? <span className="text-[#7A7A7A]"> · {r.session_type}</span> : null}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* WORKSHOPS TEASER */}
      <section data-testid="home-workshops" className="max-w-[1400px] mx-auto px-6 md:px-10 pb-28 md:pb-36">
        <Reveal>
          <Link to="/workshops" className="group block relative overflow-hidden rounded-[2rem] bg-[#F7E3D7] p-10 md:p-16">
            <div className="relative z-10 max-w-lg">
              <span className="inline-block bg-[#E4572E] text-[#F9F9F8] text-[10px] uppercase tracking-[0.25em] px-4 py-1.5 rounded-full mb-6 -rotate-2">Workshops</span>
              <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight mb-4">
                Learn photography <span className="italic text-[#E4572E]">in person.</span>
              </h2>
              <p className="text-sm text-[#4A4A4A] leading-relaxed mb-8">
                Small-group, hands-on workshops in Los Angeles — limited seats, real practice, honest feedback.
              </p>
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] border-b border-[#1A1A1A] pb-1 group-hover:gap-3 transition-all duration-300">
                See Workshops <ArrowUpRight size={13} strokeWidth={1.5} />
              </span>
            </div>
          </Link>
        </Reveal>
      </section>

      {/* CONTACT */}
      <section id="contact" data-testid="contact-section" className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-36">
        <Reveal className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.3em] text-[#7A7A7A] mb-6">Contact</p>
          <h2 className="font-serif text-5xl md:text-7xl font-light tracking-tight leading-[1.02] mb-8">
            Let's create something<br /><span className="italic">timeless.</span>
          </h2>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-[#4A4A4A]" data-testid="contact-details">
            {settings.contact_phone && (
              <a href={`sms:${settings.contact_phone}`} data-testid="contact-phone" className="inline-flex items-center gap-2 hover:text-[#1A1A1A] transition-colors">
                <Phone size={14} strokeWidth={1.5} /> {settings.contact_phone}
              </a>
            )}
            {settings.contact_email && (
              <a href={`mailto:${settings.contact_email}`} data-testid="contact-email" className="inline-flex items-center gap-2 hover:text-[#1A1A1A] transition-colors">
                <Mail size={14} strokeWidth={1.5} /> {settings.contact_email}
              </a>
            )}
            {socialLinksOf(settings).map((l, i) => {
              const Icon = socialIcon(l.url);
              return (
                <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`contact-social-${i}`} className="inline-flex items-center gap-2 hover:text-[#1A1A1A] transition-colors">
                  <Icon size={14} strokeWidth={1.5} /> {l.label || "Link"}
                </a>
              );
            })}
          </div>
        </Reveal>
        <div className="grid lg:grid-cols-2 gap-12 max-w-4xl mx-auto">
          <Reveal className="text-center lg:text-left">
            <p className="font-serif text-3xl font-light mb-4">Book a photography <span className="italic">session.</span></p>
            <p className="text-sm text-[#4A4A4A] leading-relaxed mb-8">
              For maternity, newborn, family, couples, portraits and more — the booking request form has everything I need.
            </p>
            <Link to="/book" data-testid="contact-book-btn"
              className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#F9F9F8] text-[11px] uppercase tracking-[0.22em] px-10 py-4 rounded-full hover:bg-[#4A4A4A] transition-colors duration-300">
              Book a Session <ArrowRight size={13} strokeWidth={1.5} />
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-serif text-3xl font-light mb-4 text-center lg:text-left">Anything <span className="italic">else?</span></p>
            <p className="text-sm text-[#4A4A4A] leading-relaxed mb-8 text-center lg:text-left">
              A question, a collaboration, something different — write to me here.
            </p>
            <ContactForm />
          </Reveal>
        </div>
      </section>
    </main>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/inquiries", form);
      setDone(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <p data-testid="contact-success" className="text-sm text-[#4A4A4A] leading-relaxed border border-[#1A1A1A]/10 p-6 bg-white/40">
        Thank you — your message has been received. I'll get back to you shortly.
      </p>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
      <Field label="Name" id="inquiry-name" required autoComplete="name" value={form.name} onChange={set("name")} />
      <div className="grid sm:grid-cols-2 gap-6">
        <Field label="Email" id="inquiry-email" type="email" required autoComplete="email" inputMode="email" value={form.email} onChange={set("email")} />
        <Field label="Phone" id="inquiry-phone" type="tel" optional inputMode="tel" value={form.phone} onChange={set("phone")} />
      </div>
      <TextArea label="Your message" id="inquiry-message" required rows={3} value={form.message} onChange={set("message")} />
      <SubmitButton id="inquiry-submit" loading={loading}>Send Message</SubmitButton>
    </form>
  );
}
